/**
 * Guess a business currency from what a device already knows.
 *
 * A locale's region and an IANA zone are not a choice the person made for
 * their business. They are a starting point: the setup agent uses the guess and
 * the person can name another. Never treat a miss as USD — an Indian phone
 * that reports `en-US` still sits in `Asia/Kolkata`.
 *
 * Order: the OS currency if one was handed in, then the timezone (it survives
 * a US language pack), then the locale's region.
 */
import { isCurrencyCode } from './currencies';

const EUROZONE = [
  'AD',
  'AT',
  'BE',
  'CY',
  'DE',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MC',
  'ME',
  'MT',
  'NL',
  'PT',
  'SI',
  'SK',
  'SM',
  'VA',
  'XK',
] as const;

/** ISO 3166-1 alpha-2 → ISO 4217, only codes `isCurrencyCode` accepts. */
const REGION_CURRENCY: Readonly<Record<string, string>> = {
  AE: 'AED',
  AF: 'AFN',
  AL: 'ALL',
  AM: 'AMD',
  AO: 'AOA',
  AR: 'ARS',
  AU: 'AUD',
  AZ: 'AZN',
  BD: 'BDT',
  BG: 'BGN',
  BH: 'BHD',
  BO: 'BOB',
  BR: 'BRL',
  BT: 'BTN',
  BW: 'BWP',
  BY: 'BYN',
  CA: 'CAD',
  CH: 'CHF',
  CL: 'CLP',
  CN: 'CNY',
  CO: 'COP',
  CR: 'CRC',
  CZ: 'CZK',
  DK: 'DKK',
  DZ: 'DZD',
  EG: 'EGP',
  ET: 'ETB',
  GB: 'GBP',
  GE: 'GEL',
  GH: 'GHS',
  HK: 'HKD',
  HU: 'HUF',
  ID: 'IDR',
  IL: 'ILS',
  IN: 'INR',
  IQ: 'IQD',
  IR: 'IRR',
  IS: 'ISK',
  JM: 'JMD',
  JO: 'JOD',
  JP: 'JPY',
  KE: 'KES',
  KH: 'KHR',
  KR: 'KRW',
  KW: 'KWD',
  KZ: 'KZT',
  LB: 'LBP',
  LK: 'LKR',
  MA: 'MAD',
  MM: 'MMK',
  MN: 'MNT',
  MU: 'MUR',
  MV: 'MVR',
  MX: 'MXN',
  MY: 'MYR',
  NG: 'NGN',
  NO: 'NOK',
  NP: 'NPR',
  NZ: 'NZD',
  OM: 'OMR',
  PE: 'PEN',
  PH: 'PHP',
  PK: 'PKR',
  PL: 'PLN',
  QA: 'QAR',
  RO: 'RON',
  RS: 'RSD',
  RU: 'RUB',
  SA: 'SAR',
  SE: 'SEK',
  SG: 'SGD',
  TH: 'THB',
  TN: 'TND',
  TR: 'TRY',
  TW: 'TWD',
  TZ: 'TZS',
  UA: 'UAH',
  UG: 'UGX',
  US: 'USD',
  UY: 'UYU',
  UZ: 'UZS',
  VN: 'VND',
  ZA: 'ZAR',
  ...Object.fromEntries(EUROZONE.map((region) => [region, 'EUR'])),
};

/**
 * Common IANA zones. Exact match only: `America/` is not one country, and
 * `UTC` is not a currency.
 */
const TIMEZONE_CURRENCY: Readonly<Record<string, string>> = {
  'Africa/Cairo': 'EGP',
  'Africa/Johannesburg': 'ZAR',
  'Africa/Lagos': 'NGN',
  'Africa/Nairobi': 'KES',
  'America/Argentina/Buenos_Aires': 'ARS',
  'America/Chicago': 'USD',
  'America/Denver': 'USD',
  'America/Los_Angeles': 'USD',
  'America/Mexico_City': 'MXN',
  'America/New_York': 'USD',
  'America/Sao_Paulo': 'BRL',
  'America/Toronto': 'CAD',
  'America/Vancouver': 'CAD',
  'Asia/Bahrain': 'BHD',
  'Asia/Bangkok': 'THB',
  'Asia/Calcutta': 'INR',
  'Asia/Colombo': 'LKR',
  'Asia/Dhaka': 'BDT',
  'Asia/Dubai': 'AED',
  'Asia/Hong_Kong': 'HKD',
  'Asia/Jakarta': 'IDR',
  'Asia/Karachi': 'PKR',
  'Asia/Kathmandu': 'NPR',
  'Asia/Kolkata': 'INR',
  'Asia/Kuwait': 'KWD',
  'Asia/Kuala_Lumpur': 'MYR',
  'Asia/Manila': 'PHP',
  'Asia/Muscat': 'OMR',
  'Asia/Qatar': 'QAR',
  'Asia/Riyadh': 'SAR',
  'Asia/Seoul': 'KRW',
  'Asia/Shanghai': 'CNY',
  'Asia/Singapore': 'SGD',
  'Asia/Taipei': 'TWD',
  'Asia/Tokyo': 'JPY',
  'Australia/Melbourne': 'AUD',
  'Australia/Sydney': 'AUD',
  'Europe/Amsterdam': 'EUR',
  'Europe/Berlin': 'EUR',
  'Europe/Dublin': 'EUR',
  'Europe/Istanbul': 'TRY',
  'Europe/Lisbon': 'EUR',
  'Europe/London': 'GBP',
  'Europe/Madrid': 'EUR',
  'Europe/Paris': 'EUR',
  'Europe/Rome': 'EUR',
  'Europe/Stockholm': 'SEK',
  'Europe/Warsaw': 'PLN',
  'Europe/Zurich': 'CHF',
  'Pacific/Auckland': 'NZD',
};

const asCode = (raw: string | null | undefined): string | null => {
  if (raw === null || raw === undefined) return null;
  const code = raw.trim().toUpperCase();
  return isCurrencyCode(code) ? code : null;
};

const asRegion = (raw: string | null | undefined): string | null => {
  if (raw === null || raw === undefined) return null;
  const region = raw.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(region) ? region : null;
};

/** The ISO 3166-1 region inside a BCP 47 tag (`en-IN` → `IN`), or null. */
export const regionFromLocale = (locale: string | null | undefined): string | null => {
  if (locale === null || locale === undefined) return null;
  const tag = locale.trim().replace(/_/g, '-');
  if (tag === '') return null;
  try {
    const region = new Intl.Locale(tag).region;
    const held = asRegion(region ?? null);
    if (held !== null) return held;
  } catch {
    // Hermes without a full ICU, or a tag Intl.Locale refuses.
  }
  const parts = tag.split('-');
  let at = 1;
  const script = parts[at];
  if (script !== undefined && /^[A-Za-z]{4}$/.test(script)) at += 1;
  return asRegion(parts[at]);
};

export type CurrencyGuessInput = {
  readonly timeZone?: string | null;
  readonly locale?: string | null;
  readonly region?: string | null;
  /** The OS currency when the device reports one. Strongest signal. */
  readonly localeCurrency?: string | null;
};

/** An ISO 4217 code we ship, or null when nothing on the device points at one. */
export const guessCurrency = (input: CurrencyGuessInput): string | null => {
  const fromOs = asCode(input.localeCurrency);
  if (fromOs !== null) return fromOs;

  const zone = input.timeZone?.trim();
  if (zone !== undefined && zone !== '') {
    const fromZone = asCode(TIMEZONE_CURRENCY[zone]);
    if (fromZone !== null) return fromZone;
  }

  const region = asRegion(input.region) ?? regionFromLocale(input.locale);
  if (region === null) return null;
  return asCode(REGION_CURRENCY[region]);
};
